import { PlainMessage } from "@bufbuild/protobuf";
import { useKnopClient } from "@opstel/contexts/knopClient/useKnopClient";
import { ChangeabilityRequest } from "@opstel/gen/knop/ui/v1/api/changeability_pb";
import {
  Changeability,
  toggleChangeability as toggleChangeabilityType,
} from "@opstel/query/v2/models/action";
import { instances } from "@opstel/query/queryKeys/instanceKeys";
import { useCurrentInstanceId } from "@shared/common/contexts/instanceMetadata/useInstanceId";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useToggleChangeability({
  actionId,
  currentChangeability,
}: {
  actionId: string;
  currentChangeability?: Changeability;
}) {
  const { instanceId } = useCurrentInstanceId();
  const { knopClient } = useKnopClient();
  const queryClient = useQueryClient();

  const canToggleChangeabilityQuery = useQuery({
    queryKey: instances.current(instanceId).action(actionId).changeability,
    queryFn: async () => {
      if (!instanceId || !currentChangeability) {
        return { description: "", canUpdate: false };
      }

      return await knopClient.canUpdateChangeability({
        instanceId: instanceId,
        actionGuid: actionId,
        changeability: toggleChangeabilityType(currentChangeability),
      });
    },
  });

  const mutateChangeability = useMutation({
    mutationFn: (request: PlainMessage<ChangeabilityRequest>) =>
      knopClient.postChangeability(request),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: instances.current(instanceId).action(actionId).everything,
      });
    },
    onError: (err) => console.log(err),
  });

  const toggleChangeability = () => {
    if (!instanceId || !currentChangeability) {
      console.error("Failed to toggle changeability");
      return;
    }

    mutateChangeability.mutate({
      instanceId,
      actionGuid: actionId,
      changeability: toggleChangeabilityType(currentChangeability),
    });
  };

  return {
    canToggleChangeabilityQuery,
    toggleChangeability,
  };
}
